import React, { useCallback, useMemo, useRef, useState } from 'react';

import { AiChatLauncherProvider } from './AiChatLauncherContext';
import AiOpsChatIframeDrawer, { type AiChatPromptRequest } from './AiOpsChatIframeDrawer';
import { AiToolsContext, type AiToolsSectionRegistration } from './AiToolsContext';

export function isAiChatDisabled(): boolean {
  return window.configs?.AI_CHAT_DISABLED === 'true';
}

interface Props {
  children?: React.ReactNode;
  hidden?: boolean;
}

export default function AiChatEntry({ children, hidden = false }: Props) {
  const isAvailable = !hidden && !isAiChatDisabled() && Boolean(window.configs?.AI_OPS_CHAT_ORIGIN);
  const [promptRequest, setPromptRequest] = useState<AiChatPromptRequest | null>(null);
  const [toolSection, setToolSection] = useState<AiToolsSectionRegistration | null>(null);
  const [toolsContainer, setToolsContainer] = useState<HTMLDivElement | null>(null);
  const registerSection = useCallback((section: AiToolsSectionRegistration) => {
    setToolSection(section);
    return () => setToolSection((current) => (current?.id === section.id ? null : current));
  }, []);
  const tools = useMemo(
    () => ({ isAvailable, container: toolsContainer, registerSection }),
    [isAvailable, toolsContainer, registerSection],
  );
  const nextRequestIdRef = useRef(1);

  const startChatWithPrompt = useCallback((prompt: string, options?: { fallbackDepartment?: string }) => {
    setPromptRequest({
      prompt,
      requestId: nextRequestIdRef.current++,
      fallbackDepartment: options?.fallbackDepartment ?? null,
    });
  }, []);

  const launcher = useMemo(() => ({ isAvailable, startChatWithPrompt }), [isAvailable, startChatWithPrompt]);

  return (
    <AiChatLauncherProvider value={launcher}>
      <AiToolsContext.Provider value={tools}>
        {children}
        {isAvailable ? (
          <AiOpsChatIframeDrawer
            promptRequest={promptRequest}
            toolSection={toolSection}
            onToolsContainer={setToolsContainer}
          />
        ) : null}
      </AiToolsContext.Provider>
    </AiChatLauncherProvider>
  );
}
